import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  BarChart3,
  LineChart as LineChartIcon,
  PieChart as PieChartIcon,
  Activity,
  Save,
  Play,
  Loader2,
  FileText,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  useReportMetrics,
  useReportDimensions,
  useChartTypes,
  useCreateReport,
  useRunReport,
} from '../../hooks/useAnalytics';
import { CreateReportRequest, analyticsService } from '../../services/analytics.service';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { useAuthStore } from '@stores/authStore';
import { ORG_WIDE_DATA_SOURCES, canAdministerOrg, isOrgWideTemplate } from '@/lib/roles';

const STEPS = [
  { id: 'source', label: 'Data Source' },
  { id: 'metrics', label: 'Metrics' },
  { id: 'config', label: 'Configuration' },
  { id: 'preview', label: 'Preview & Save' },
];

// #18950 / BUG-043 — only the data sources the analytics Report Builder
// actually defines metrics for (report_builder.py AVAILABLE_METRICS). The
// previously-listed automations / pipelines / custom_metrics sources have no
// report-builder metrics or generators, so every one of them showed "No
// metrics available" at step 2. They can be re-added here once the backend
// Report Builder supports them (automation/pipeline analytics currently live in
// the separate custom-metrics subsystem, not the Report Builder).
const DATA_SOURCES = [
  { id: 'tickets', label: 'Tickets', description: 'Ticket volume, status, and resolution metrics', icon: BarChart3 },
  { id: 'sla', label: 'SLA', description: 'SLA compliance and breach metrics', icon: Activity },
  { id: 'channels', label: 'Channels', description: 'Channel distribution and performance', icon: PieChartIcon },
  { id: 'agents', label: 'Agents', description: 'Agent performance and productivity', icon: LineChartIcon },
];

/** Built-in report templates available when the API has none */
const BUILT_IN_TEMPLATES = [
  {
    id: 'tpl_exec_summary',
    name: 'Executive Weekly Summary',
    description: 'Volume, CSAT, SLA, AI savings, and trends for leadership',
    data_source: 'tickets',
    metrics: ['total_tickets', 'resolved_tickets', 'avg_response_time', 'sla_compliance'],
    dimensions: ['status'],
    chart_type: 'bar',
  },
  {
    id: 'tpl_agent_scorecard',
    name: 'Agent Scorecard',
    description: 'Per-agent KPIs with peer comparison and improvement areas',
    data_source: 'agents',
    metrics: ['tickets_resolved', 'avg_response_time', 'sla_compliance', 'satisfaction'],
    dimensions: ['agent'],
    chart_type: 'bar',
  },
  {
    id: 'tpl_sla_health',
    name: 'SLA Health Check',
    description: 'SLA breaches by priority, at-risk tickets, and root causes',
    data_source: 'sla',
    metrics: ['first_response_compliance', 'resolution_compliance', 'breached_tickets'],
    dimensions: ['priority'],
    chart_type: 'bar',
  },
  {
    id: 'tpl_channel_efficiency',
    name: 'Channel Efficiency',
    description: 'Cost and resolution metrics per support channel',
    data_source: 'channels',
    metrics: ['ticket_count', 'avg_resolution_time'],
    dimensions: ['channel'],
    chart_type: 'pie',
  },
  {
    id: 'tpl_automation_roi',
    name: 'Automation ROI',
    description: 'Rules fired, success rate, time saved, manual vs auto',
    data_source: 'automations',
    metrics: ['total_executions', 'completed', 'failed', 'success_rate'],
    dimensions: [],
    chart_type: 'bar',
  },
  {
    id: 'tpl_pipeline_bottleneck',
    name: 'Pipeline Bottleneck Analysis',
    description: 'Average time in each pipeline stage to find bottlenecks',
    data_source: 'pipelines',
    metrics: ['stage_count', 'avg_time_in_stage'],
    dimensions: ['stage'],
    chart_type: 'bar',
  },
];

const CHART_ICONS: Record<string, React.ElementType> = {
  bar: BarChart3,
  line: LineChartIcon,
  pie: PieChartIcon,
  area: Activity,
};

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

export default function ReportBuilder() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [reportName, setReportName] = useState('');
  const [dataSource, setDataSource] = useState('');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);
  const [chartType, setChartType] = useState('bar');
  const [timeRange, setTimeRange] = useState<{ start: string; end: string } | null>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  // An agent reports on their own work only: leadership summaries, agent
  // comparisons and organization-wide sources are not offered to them.
  const authUser = useAuthStore((st) => st.user);
  const isAgent = !canAdministerOrg(authUser) && (authUser?.role || '').toLowerCase() !== 'supervisor';
  const offeredTemplates = useMemo(
    () => (isAgent ? templates.filter((t: any) => !isOrgWideTemplate(t)) : templates),
    [templates, isAgent],
  );
  const offeredSources = useMemo(
    () => (isAgent ? DATA_SOURCES.filter((src) => !ORG_WIDE_DATA_SOURCES.has(src.id)) : DATA_SOURCES),
    [isAgent],
  );

  // Load templates on mount — fall back to built-in templates if API returns empty
  useState(() => {
    analyticsService.listTemplates().then((t) => {
      setTemplates(t && t.length > 0 ? t : BUILT_IN_TEMPLATES);
      setTemplatesLoaded(true);
    }).catch(() => {
      setTemplates(BUILT_IN_TEMPLATES);
      setTemplatesLoaded(true);
    });
  });

  const handleTemplateSelect = (template: any) => {
    setDataSource(template.data_source);
    setSelectedMetrics(template.metrics);
    setSelectedDimensions(template.dimensions || []);
    setChartType(template.chart_type);
    setReportName(template.name);
    setCurrentStep(3); // Jump to preview step
  };

  // Fetch metadata
  const { data: metricsData, isLoading: metricsLoading } = useReportMetrics(dataSource || undefined);
  const { data: dimensionsData } = useReportDimensions();
  const { data: chartTypesData } = useChartTypes();

  // Mutations
  const createReport = useCreateReport();
  const runReport = useRunReport();

  // Available metrics for selected data source
  const availableMetrics = useMemo(() => {
    if (!metricsData || !dataSource) return [];
    // The per-source endpoint answers { data_source, metrics: [...] }. The
    // bare-array and { [source]: [...] } shapes are kept for older responses.
    if (Array.isArray(metricsData)) return metricsData;
    if (Array.isArray(metricsData.metrics)) return metricsData.metrics;
    return metricsData.metrics?.[dataSource] || metricsData[dataSource] || [];
  }, [metricsData, dataSource]);

  const toggleMetric = (metric: string) => {
    setSelectedMetrics(prev =>
      prev.includes(metric) ? prev.filter(m => m !== metric) : [...prev, metric],
    );
  };

  const toggleDimension = (dim: string) => {
    setSelectedDimensions(prev =>
      prev.includes(dim) ? prev.filter(d => d !== dim) : [...prev, dim],
    );
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return !!dataSource;
      case 1: return selectedMetrics.length > 0;
      case 2: return !!chartType;
      case 3: return !!reportName.trim();
      default: return false;
    }
  };

  const handleSave = async () => {
    const payload: CreateReportRequest = {
      name: reportName,
      data_source: dataSource,
      metrics: selectedMetrics,
      dimensions: selectedDimensions.length > 0 ? selectedDimensions : undefined,
      time_range: timeRange || undefined,
      chart_type: chartType,
    };

    try {
      const report = await createReport.mutateAsync(payload);
      // Optionally run immediately
      navigate('/reports/saved');
    } catch (err) {
      console.error('Failed to create report:', err);
    }
  };

  const handleSaveAndRun = async () => {
    const payload: CreateReportRequest = {
      name: reportName,
      data_source: dataSource,
      metrics: selectedMetrics,
      dimensions: selectedDimensions.length > 0 ? selectedDimensions : undefined,
      time_range: timeRange || undefined,
      chart_type: chartType,
    };

    try {
      const report = await createReport.mutateAsync(payload);
      await runReport.mutateAsync({ id: report.id, dateRange: timeRange || undefined });
      navigate('/reports/saved');
    } catch (err) {
      console.error('Failed to create/run report:', err);
    }
  };

  // Default date range for time range picker
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  }, []);
  const defaultEnd = useMemo(() => new Date().toISOString().split('T')[0], []);

  return (
    <div className="p-4 lg:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
<div className="border border-[var(--co-line)] bg-[var(--co-raised)] px-5 py-[18px]" style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}>
      <div className="flex items-center justify-between gap-[10px]">
        <div className="flex min-w-0 items-center gap-4">
        <button
          onClick={() => navigate('/reports')}
          className="p-2 rounded-[10px] hover:bg-[var(--co-selected)] transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-[var(--co-ink-3)]" />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-[var(--co-ink)]">Report Builder</h1>
          <p className="text-sm text-[var(--co-ink-3)]">Create a custom analytics report</p>
        </div>
        </div>
        <div className="flex h-[34px] flex-none items-center">
          <PageHeaderMeta />
        </div>
      </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, idx) => (
          <div key={step.id} className={`flex items-center ${idx < STEPS.length - 1 ? 'flex-1' : ''}`}>
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  idx < currentStep
                    ? 'bg-primary-600 text-white'
                    : idx === currentStep
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 ring-2 ring-primary-600'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                {idx < currentStep ? <Check className="h-4 w-4" /> : idx + 1}
              </div>
              <span
                className={`text-sm font-medium hidden sm:inline ${
                  idx <= currentStep ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-3 ${idx < currentStep ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="card bg-white dark:bg-gray-800 p-6 min-h-[400px]">
        {/* Step 0: Data Source */}
        {currentStep === 0 && (
          <div className="space-y-6">
            {/* Template Quick Start */}
            {templatesLoaded && offeredTemplates.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Start from Template</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Quick start with a pre-configured report template</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {offeredTemplates.map((template: any) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className="p-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/10 text-left transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{template.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{template.description}</p>
                      <span className="inline-block mt-2 px-2 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 capitalize">
                        {template.data_source}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">or build from scratch</span>
                  </div>
                </div>
              </div>
            )}

            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Choose Data Source</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Select the primary data source for your report</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {offeredSources.map((src) => {
                const Icon = src.icon;
                return (
                  <button
                    key={src.id}
                    onClick={() => {
                      setDataSource(src.id);
                      setSelectedMetrics([]);
                    }}
                    className={`p-4 rounded-lg border-2 text-left transition-colors sm:last:odd:col-span-2 ${
                      dataSource === src.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${dataSource === src.id ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600' : 'bg-gray-100 text-gray-500 dark:bg-gray-700'} dark:text-gray-400`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{src.label}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{src.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 1: Metrics */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Select Metrics</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Choose the metrics to include in your report ({selectedMetrics.length} selected)
            </p>
            {metricsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
              </div>
            ) : availableMetrics.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {availableMetrics.map((metric: any) => {
                  const metricName = typeof metric === 'string' ? metric : metric.name;
                  const metricDesc = typeof metric === 'string' ? '' : metric.description;
                  const isSelected = selectedMetrics.includes(metricName);
                  return (
                    <button
                      key={metricName}
                      onClick={() => toggleMetric(metricName)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {metricName.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                          </p>
                          {metricDesc && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{metricDesc}</p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
                No metrics available for the selected data source. The analytics service may need to be started.
              </div>
            )}
          </div>
        )}

        {/* Step 2: Configuration */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Configure Report</h2>

            {/* Chart Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Chart Type
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {['bar', 'line', 'area', 'pie'].map((type) => {
                  const Icon = CHART_ICONS[type] || BarChart3;
                  return (
                    <button
                      key={type}
                      onClick={() => setChartType(type)}
                      className={`p-3 rounded-lg border text-center transition-colors ${
                        chartType === type
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`h-6 w-6 mx-auto mb-1 ${chartType === type ? 'text-primary-600' : 'text-gray-400'} dark:text-gray-500`} />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{type}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Dimensions */}
            {dimensionsData && Array.isArray(dimensionsData) && dimensionsData.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Group By (optional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {dimensionsData.map((dim: any) => {
                    const dimName = typeof dim === 'string' ? dim : dim.name;
                    const isSelected = selectedDimensions.includes(dimName);
                    return (
                      <button
                        key={dimName}
                        onClick={() => toggleDimension(dimName)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          isSelected
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {dimName.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Time Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Time Range (optional)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="date"
                  value={timeRange?.start || defaultStart}
                  onChange={(e) => setTimeRange({ start: e.target.value, end: timeRange?.end || defaultEnd })}
                  className="input-field text-sm"
                />
                <span className="text-gray-400 dark:text-gray-500">to</span>
                <input
                  type="date"
                  value={timeRange?.end || defaultEnd}
                  onChange={(e) => setTimeRange({ start: timeRange?.start || defaultStart, end: e.target.value })}
                  className="input-field text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Preview & Save */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Preview & Save</h2>

            {/* Report Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Report Name
              </label>
              <input
                type="text"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="e.g. Weekly Ticket Volume"
                className="input-field w-full"
              />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-2">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Report Summary</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-500 dark:text-gray-400">Data Source</div>
                <div className="text-gray-900 dark:text-white capitalize">{dataSource}</div>
                <div className="text-gray-500 dark:text-gray-400">Metrics</div>
                <div className="text-gray-900 dark:text-white">{selectedMetrics.length} selected</div>
                <div className="text-gray-500 dark:text-gray-400">Chart Type</div>
                <div className="text-gray-900 dark:text-white capitalize">{chartType}</div>
                {selectedDimensions.length > 0 && (
                  <>
                    <div className="text-gray-500 dark:text-gray-400">Grouped By</div>
                    <div className="text-gray-900 dark:text-white">{selectedDimensions.join(', ')}</div>
                  </>
                )}
                {timeRange && (
                  <>
                    <div className="text-gray-500 dark:text-gray-400">Time Range</div>
                    <div className="text-gray-900 dark:text-white">{timeRange.start} to {timeRange.end}</div>
                  </>
                )}
              </div>
            </div>

            {/* Chart Preview Placeholder */}
            <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <BarChart3 className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Chart preview will be available after saving and running the report
              </p>
            </div>

            {/* Selected Metrics List */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Selected Metrics</h3>
              <div className="flex flex-wrap gap-2">
                {selectedMetrics.map((m) => (
                  <span
                    key={m}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                  >
                    {m.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          disabled={currentStep === 0}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center gap-3">
          {currentStep === STEPS.length - 1 ? (
            <>
              <button
                onClick={handleSave}
                disabled={!canProceed() || createReport.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                {createReport.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </button>
              <button
                onClick={handleSaveAndRun}
                disabled={!canProceed() || createReport.isPending || runReport.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {(createReport.isPending || runReport.isPending) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                Save & Run
              </button>
            </>
          ) : (
            <button
              onClick={() => setCurrentStep(Math.min(STEPS.length - 1, currentStep + 1))}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {createReport.isError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
          Failed to create report. Please check that the analytics service is running.
        </div>
      )}
    </div>
  );
}
